import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import { usePageSeo, SITE_ORIGIN } from "@/hooks/usePageSeo";
import { articles, categories } from "@/mocks/articles";

type SortKey = "heat" | "newest" | "discuss";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "热瓜榜 - 诱惑吃瓜",
  url: `${SITE_ORIGIN}/hot`,
  inLanguage: "zh-CN",
  description: "按讨论热度实时排序的娱乐话题瓜线合集，支持分类筛选与关键词搜索。",
  isPartOf: { "@type": "WebSite", name: "诱惑吃瓜", url: SITE_ORIGIN },
};

export default function Hot() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("cat") ?? "all";
  const keyword = params.get("q") ?? "";
  const [sort, setSort] = useState<SortKey>("heat");
  const [input, setInput] = useState(keyword);

  usePageSeo({
    title: "热瓜榜 - 实时娱乐话题排行 | 诱惑吃瓜",
    description:
      "诱惑吃瓜热瓜榜按讨论热度实时排序今日娱乐话题，支持明星动向、综艺现场、影视热点、行业揭秘等分类筛选与关键词搜索，附时间线与最新进展。",
    keywords: "热瓜榜,娱乐话题排行,吃瓜榜单,综艺路透,影视热点,反转复盘",
    path: "/hot",
    type: "website",
    jsonLd,
  });

  const filtered = useMemo(() => {
    let list = articles.filter((article) => {
      const matchCat = activeCat === "all" || article.categoryId === activeCat;
      const matchKeyword =
        keyword.trim() === "" ||
        article.title.includes(keyword.trim()) ||
        article.excerpt.includes(keyword.trim()) ||
        article.tags.some((tag) => tag.includes(keyword.trim()));
      return matchCat && matchKeyword;
    });
    list = [...list].sort((a, b) => {
      if (sort === "newest") return b.date.localeCompare(a.date);
      if (sort === "discuss") return b.comments - a.comments;
      return b.heat - a.heat;
    });
    return list;
  }, [activeCat, keyword, sort]);

  const applyCat = (cat: string) => {
    const next = new URLSearchParams(params);
    if (cat === "all") next.delete("cat");
    else next.set("cat", cat);
    setParams(next, { replace: true });
  };

  const applySearch = (value: string) => {
    const next = new URLSearchParams(params);
    if (value.trim() === "") next.delete("q");
    else next.set("q", value.trim());
    setParams(next, { replace: true });
  };

  const currentCat = categories.find((c) => c.id === activeCat);

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />
      <main className="flex-1 pt-16 md:pt-20">
        <section className="w-full px-4 md:px-10 pt-8 pb-10 md:pt-12 md:pb-14 bg-background-100/60">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "热瓜榜" }]} />
          <div className="mt-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-2xl">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950">
                热瓜榜：此刻最值得追的瓜线
              </h1>
              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                {currentCat && currentCat.id !== "all"
                  ? `正在浏览「${currentCat.name}」：${currentCat.desc}`
                  : "所有正在发酵的话题都在这里，按热度排序，支持分类筛选与关键词搜索，每一条都附了背景与后续追踪。"}
              </p>
            </div>
            <div className="flex items-center gap-3 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-background-50 border border-background-200">
                <i className="ri-file-list-3-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                共 {filtered.length} 条瓜线
              </span>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-10 py-8 md:py-10">
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-2">
              {categories.map((cat) => {
                const isActive = activeCat === cat.id;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => applyCat(cat.id)}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border transition-colors whitespace-nowrap ${
                      isActive
                        ? "bg-primary-500 border-primary-500 text-background-50"
                        : "bg-background-50 border-background-200 text-foreground-700 hover:border-primary-300"
                    }`}
                  >
                    <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
                    {cat.name}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <form
                className="flex items-center gap-2 w-full sm:max-w-md"
                onSubmit={(e) => {
                  e.preventDefault();
                  applySearch(input);
                }}
                role="search"
              >
                <div className="flex items-center gap-2 flex-1 h-11 px-3.5 rounded-md bg-background-50 border border-background-200 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100 transition-colors">
                  <i className="ri-search-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="搜索瓜线标题、标签…"
                    aria-label="搜索瓜线"
                    className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                  />
                </div>
                <button
                  type="submit"
                  className="h-11 px-5 rounded-md bg-secondary-500 text-background-50 text-sm hover:bg-secondary-600 transition-colors whitespace-nowrap"
                >
                  搜索
                </button>
              </form>

              <div className="flex items-center gap-2 text-sm">
                <span className="text-foreground-500">排序：</span>
                <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
                  {[
                    { key: "heat" as SortKey, label: "热度" },
                    { key: "newest" as SortKey, label: "最新" },
                    { key: "discuss" as SortKey, label: "讨论" },
                  ].map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setSort(item.key)}
                      className={`px-3.5 py-1.5 rounded-full text-xs transition-colors whitespace-nowrap ${
                        sort === item.key
                          ? "bg-background-50 text-primary-600 font-medium"
                          : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {filtered.length > 0 ? (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          ) : (
            <div className="mt-12 flex flex-col items-center text-center py-16 rounded-lg border border-dashed border-background-300">
              <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-2xl text-foreground-400">
                <i className="ri-search-eye-line w-8 h-8 flex items-center justify-center"></i>
              </span>
              <h3 className="mt-4 text-lg font-semibold text-foreground-900">没有找到相关瓜线</h3>
              <p className="mt-2 text-sm text-foreground-500">换个关键词，或者切换分类看看其他正在发酵的话题。</p>
              <button
                type="button"
                onClick={() => {
                  setInput("");
                  setParams(new URLSearchParams(), { replace: true });
                }}
                className="mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                重置筛选
              </button>
            </div>
          )}
        </section>

        <section className="w-full px-4 md:px-10 pb-16 md:pb-24">
          <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-5">
            <div>
              <h2 className="text-lg md:text-xl font-semibold text-foreground-950">
                想第一时间蹲到后续？
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                进聊天室和同好一起等进展，反转来了大家一起看。
              </p>
            </div>
            <Link
              to="/chat"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-chat-smile-2-line w-4 h-4 flex items-center justify-center"></i>
              进聊天室
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}